<template>
    <BaseDialog v-bind="$attrs" :title="settingsTitle" width="narrow" @close="closeDialog">
        <p v-if="!userDetails.has_calendar_integration" class="body-small">{{ t("components.calendar.settings.intro") }}</p>
        <label class="flex flex-col gap-2" :for="timezoneInputId">
            <span class="body-small">{{ t("components.calendar.settings.timeZone") }}</span>
            <BaseCombobox
                v-model="selectedTimezone"
                :input-id="timezoneInputId"
                :clearable="false"
                :options="calendarIntegration.timezones.map((timezone) => ({ label: timezone, value: timezone }))">
            </BaseCombobox>
        </label>
        <div class="flex flex-col gap-2">
            <div class="body-small">
                {{ t("components.calendar.settings.connection") }}
                <span v-if="!userDetails.has_calendar_integration" class="ml-2 body-small text-ink-muted">{{ " " + t("components.calendar.settings.disconnectAnytime") }}</span>
            </div>
            <CalendarIntegrationDisconnect @close="closeDialog" />
        </div>
        <div v-if="!!calendarIntegration?.private_toggle_enabled" class="flex flex-col gap-2 w-full">
            <p v-if="!userDetails.has_calendar_integration" class="body-small text-ink-muted">{{ t("components.calendar.settings.privateEventsDefault") }}</p>

            <label class="body-small flex items-center gap-2" for="private-event">
                {{ t("components.calendar.settings.includePrivateEvents") }}
                <BaseSwitch id="private-event" v-model="allowPrivateEvents" size="sm" />
            </label>
        </div>
        <template #footer>
            <BaseButton
                v-if="!userDetails.has_calendar_integration && hasCalendarProvider"
                class="flex items-center justify-center gap-2"
                theme="primary"
                :disabled="!hasCalendarProvider || !isTimezoneSelected || isFetchingAuthUrl || calendarPollingActive"
                @click="startCalendarIntegration()">
                <LoadingSpinner v-if="isFetchingAuthUrl || calendarPollingActive" class="w-4 h-4" />
                <span>{{ calendarPollingActive ? t("components.calendar.settings.connecting") : t("components.calendar.settings.confirmAndConnect") }}</span>
            </BaseButton>
            <p v-else-if="!userDetails.has_calendar_integration && !hasCalendarProvider" class="body-small text-ink-muted mt-2 text-center">
                {{ t("components.calendar.settings.unavailable") }}
            </p>
            <BaseButton v-else theme="primary" :disabled="!isTimezoneSelected" @click="handleSave">{{ t("common.save") }}</BaseButton>
        </template>
    </BaseDialog>
    <BaseToast v-model:open="isErrorToastOpen" theme="error">
        <template #description> <MarkdownText class="py-1" :text="errorToastMsgWithEmail" /> </template>
    </BaseToast>
</template>

<script setup lang="ts">
import { Browser } from "@capacitor/browser";
import { router, usePage } from "@inertiajs/vue3";
import { useTimeoutPoll } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { UserDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCombobox from "~vue/components/BaseCombobox.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseSwitch from "~vue/components/BaseSwitch.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import type { CalendarIntegration } from "~vue/types/chat_home";
import { isInIFrame } from "~vue/utils";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, onMounted, type Ref, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";

import BaseToast from "../BaseToast.vue";
import CalendarIntegrationDisconnect from "./CalendarIntegrationDisconnect.vue";

const { t } = useI18n();
const settingsTitle = computed(() =>
    userDetails.value?.has_calendar_integration ? t("components.calendar.settings.settingsTitle") : t("components.calendar.settings.connectionTitle"),
);

const isMobileApp = usePage().props.is_mobile_app;
const props = defineProps<{
    // Names the connect surface for calendar_integration_started, which the auth-url view logs.
    source: string;
    deeplinkSource?: string;
}>();
const emit = defineEmits<{ close: []; "timezone-updated": [] }>();
const userDetails = inject<Ref<UserDetails>>("user_details")!;
const calendarIntegration = inject<CalendarIntegration>("calendarIntegration")!;
// The connect flow builds /calendar_integration/<provider>/auth-url/ from the org's provider.
// If the org has no provider configured, that request is null (ENG-1423), so gate the connect
// affordance on a provider being present. The CALENDAR_INTEGRATION flag can be on without a
// provider (global default / rollout), so the flag alone is not a sufficient guard.
const hasCalendarProvider = computed(() => Boolean(calendarIntegration?.calendar_provider));
const selectedTimezone = ref<{ label: string; value: string } | null>(null);
const timezoneInputId = useId();
const allowPrivateEvents = ref(calendarIntegration?.allow_private_events ?? !calendarIntegration?.private_toggle_enabled);
const updatePrivateEventPayload = computed(() => ({
    allow_private_events: !!allowPrivateEvents.value,
}));

const authUrl = computed(() => {
    const params = new URLSearchParams();
    if (isInIFrame()) {
        params.set("teams", "true");
    }
    params.set("allow_private_events", String(allowPrivateEvents.value));
    const currentPath = globalThis.location.pathname;
    if (currentPath !== "/") {
        params.set("next", currentPath);
    }
    params.set("source", props.source);
    if (props.deeplinkSource) {
        params.set("deeplink_source", props.deeplinkSource);
    }
    return `/calendar_integration/${calendarIntegration?.calendar_provider}/auth-url/?${params}`;
});
const { data: authData, execute: fetchAuthUrl, isFetching: isFetchingAuthUrl, onFetchError: onFetchingAuthError } = useFetch(authUrl, { immediate: false }).get().json();

// Refer to ADR 8 for more info about MS Teams Oauth for Calendar Integration
const { execute: checkStatus, data: statusData } = useFetch("/api/calendar_integration/status/", { immediate: false }).get().json();

const checkCalendarIntegrationComplete = async () => {
    try {
        await checkStatus();
        return statusData.value?.calendarIntegrationStatus || false;
    } catch {
        return false;
    }
};

const isErrorToastOpen = ref(false);
const errorToastMsg = ref("");
const errorToastMsgWithEmail = computed(
    () =>
        `${t("components.calendar.settings.contactSupport", {
            message: errorToastMsg.value,
        })} [hi@valence.co](mailto:hi@valence.co)`,
);

onFetchingAuthError(() => {
    isErrorToastOpen.value = true;
    errorToastMsg.value = t("components.calendar.settings.connectError");
    logUserInteraction("calendar_integration_error", { calendar_error_type: "auth_url_fetch_error" });
});

const timezonePayload = computed(() => ({ timezone: selectedTimezone.value?.value }));
const { execute: updateTimezone } = useFetch("/api/accounts/user", { immediate: false }).put(timezonePayload).json();

const isTimezoneSelected = computed(() => selectedTimezone.value?.value != null);

const {
    resume: startPolling,
    pause: stopPolling,
    isActive: calendarPollingActive,
} = useTimeoutPoll(
    checkCalendarIntegrationComplete,
    2000, // Poll every 2 seconds
    {
        immediate: false,
    },
);

watch(statusData, (data) => {
    if (data?.calendarIntegrationStatus === true) {
        stopPolling();
        if (isMobileApp) {
            Browser.close();
        }
        globalThis.location.reload();
    }
});

const startCalendarIntegration = () => {
    updateUserTimezone();
    redirectForCalendarIntegration();
};

const redirectForCalendarIntegration = async () => {
    // Never build/fetch /calendar_integration/<provider>/auth-url/ without a provider (ENG-1423).
    if (!hasCalendarProvider.value) {
        return;
    }
    await fetchAuthUrl();

    if (authData.value?.auth_url) {
        if (isMobileApp) {
            // Mobile app: open in SFSafariViewController via Capacitor Browser plugin.
            // Poll for completion — the OAuth callback closes the browser automatically.
            Browser.open({ url: authData.value.auth_url });
            startPolling();
        } else if (isInIFrame()) {
            // Use magic login URL if available for Teams, otherwise fall back to auth URL
            const popupUrl = authData.value.magic_login_url || authData.value.auth_url;
            globalThis.open(popupUrl, "_blank");
            startPolling();
        } else {
            // Standard web flow - redirect in same window
            globalThis.location.href = authData.value.auth_url;
        }
    }
};

const updateUserTimezone = async () => {
    await updateTimezone();
    emit("timezone-updated");
    router.reload({ only: ["user_details"] });
};

const updateUserPrivateEvents = async () => {
    await updatePrivateEvents();
    router.reload({ only: ["user_details"] });
};

const handleSave = async () => {
    const promises = [];

    if (isTimezoneSelected.value) {
        promises.push(updateUserTimezone());
    }

    promises.push(updateUserPrivateEvents());

    await Promise.all(promises);
    closeDialog();
};

const {
    execute: updatePrivateEvents,
    onFetchResponse: onUpdatePrivateEventsResponse,
    onFetchError: onUpdatePrivateEventsError,
    data: privateEventsToggleState,
} = useFetch("/api/calendar_integration/credential/", { immediate: false }).patch(updatePrivateEventPayload).json();

onUpdatePrivateEventsResponse(() => {
    allowPrivateEvents.value = privateEventsToggleState.value.allow_private_events;
});

onUpdatePrivateEventsError(() => {
    errorToastMsg.value = t("components.calendar.settings.privateEventsError");
    isErrorToastOpen.value = true;
    logUserInteraction("calendar_integration_error", { calendar_error_type: "private_event_toggle_error" });
});

const closeDialog = () => {
    emit("close");
};

onMounted(() => {
    if (userDetails.value.timezone) {
        selectedTimezone.value = { label: userDetails.value.timezone, value: userDetails.value.timezone };
    }
});
</script>
